import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import {
  useSupportChatStore,
  type ChatMessage,
  type ChatParticipant,
} from "../../../store/supportChatStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPaperPlane,
  faImage,
  faPaperclip,
  faSpinner,
  faUser,
  faUsers,
  faCircle,
  faTruck,
  faTimes,
  faDownload,
  faFile,
  faClock,
  faExclamationCircle,
  faRotateRight,
  faCheck,
  faRoute,
} from "@fortawesome/free-solid-svg-icons";
import ConversationDetailsPanel from "./ConversationDetailsPanel";

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const NEAR_BOTTOM_PX = 200;
const LOAD_OLDER_TRIGGER_PX = 80;
const CHAT_INPUT_MIN_PX = 40;
const CHAT_INPUT_MAX_PX = 96;

const formatBytes = (bytes: number): string => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Force-download a remote file via blob. The HTML `download` attribute is
// ignored cross-origin (our files live on storage.googleapis.com), so the
// browser navigates instead of saving — fetching as blob bypasses that.
async function downloadRemoteFile(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url, { credentials: "omit" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a tick to give the browser time to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error("[ChatRoom] Download failed, falling back to navigation:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

interface ChatRoomProps {
  conversationId: string;
  onBack: () => void;
}

export default function ChatRoom({ conversationId, onBack }: ChatRoomProps) {
  const { t } = useTranslation("support");
  const { isDarkMode } = useTheme();
  const user = useUserStore((state) => state.user);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);

  // Scroll bookkeeping
  const firstMessageIdRef = useRef<string | null>(null);
  const prevScrollMetricsRef = useRef({ scrollHeight: 0, scrollTop: 0 });
  /** After opening a conversation, scroll once to the latest message. */
  const didInitialScrollRef = useRef<string | null>(null);

  const messages = useSupportChatStore((s) => s.messages);
  const conversations = useSupportChatStore((s) => s.conversations);
  const isLoadingMessages = useSupportChatStore((s) => s.isLoadingMessages);
  const isLoadingOlderMessages = useSupportChatStore((s) => s.isLoadingOlderMessages);
  const hasMoreMessages = useSupportChatStore((s) => s.hasMoreMessages);
  const onlineUsers = useSupportChatStore((s) => s.onlineUsers);
  const sendMessage = useSupportChatStore((s) => s.sendMessage);
  const markAsRead = useSupportChatStore((s) => s.markAsRead);
  const loadOlderMessages = useSupportChatStore((s) => s.loadOlderMessages);
  const uploadFile = useSupportChatStore((s) => s.uploadFile);
  const retrySendMessage = useSupportChatStore((s) => s.retrySendMessage);
  const removeFailedMessage = useSupportChatStore((s) => s.removeFailedMessage);

  const [showDetails, setShowDetails] = useState(false);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);

  // Memoize derived data so we don't allocate new arrays / scan participants
  // on every keystroke or message update.
  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === conversationId),
    [conversations, conversationId],
  );

  const others = useMemo<ChatParticipant[]>(
    () => activeConversation?.participants.filter((p) => p.uid !== user?.id) ?? [],
    [activeConversation?.participants, user?.id],
  );

  const isOtherOnline = useMemo(
    () => others.some((p) => onlineUsers[p.uid]),
    [others, onlineUsers],
  );

  const convName = useMemo(() => {
    const participants = activeConversation?.participants ?? [];
    const name = activeConversation?.name;
    if (name) return name;
    const otherParticipants = participants.filter((p) => p.uid !== user?.id);
    if (otherParticipants.length === 0) return t("chat.you");
    return otherParticipants.map((p) => p.display_name).join(", ");
  }, [activeConversation?.participants, activeConversation?.name, user?.id, t]);

  // Index messages by id once per change so the prepend detection in the
  // layout effect is O(1) instead of O(n) on every render with long histories.
  const messageIdSet = useMemo(
    () => new Set(messages.map((m) => m.id)),
    [messages],
  );

  // Smart scroll: keep position on prepend (older history), follow on new messages.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const newFirstId = messages[0]?.id ?? null;
    const prevFirstId = firstMessageIdRef.current;
    const prepended =
      !!prevFirstId &&
      !!newFirstId &&
      prevFirstId !== newFirstId &&
      messageIdSet.has(prevFirstId);

    if (prepended) {
      const diff = container.scrollHeight - prevScrollMetricsRef.current.scrollHeight;
      container.scrollTop = prevScrollMetricsRef.current.scrollTop + diff;
    } else {
      const wasNearBottom =
        prevScrollMetricsRef.current.scrollHeight === 0 ||
        prevScrollMetricsRef.current.scrollHeight -
          prevScrollMetricsRef.current.scrollTop -
          container.clientHeight <
          NEAR_BOTTOM_PX;
      if (wasNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }

    firstMessageIdRef.current = newFirstId;
    prevScrollMetricsRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
  }, [messages, messageIdSet]);

  // Reset scroll tracking when switching conversation
  useEffect(() => {
    firstMessageIdRef.current = null;
    prevScrollMetricsRef.current = { scrollHeight: 0, scrollTop: 0 };
    didInitialScrollRef.current = null;
    setUploadError(null);
  }, [conversationId]);

  // Snap to bottom once when messages first load for this conversation (better
  // than relying only on layout effect when scrollHeight was still 0).
  useEffect(() => {
    if (isLoadingMessages || messages.length === 0) return;
    if (didInitialScrollRef.current === conversationId) return;
    didInitialScrollRef.current = conversationId;
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [conversationId, isLoadingMessages, messages.length]);

  // Mark as read when entering
  useEffect(() => {
    if (user?.id && conversationId) {
      markAsRead(conversationId);
    }
  }, [conversationId, user?.id, markAsRead]);

  // Infinite scroll: load older messages when near top
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (
      container.scrollTop < LOAD_OLDER_TRIGGER_PX &&
      hasMoreMessages &&
      !isLoadingOlderMessages &&
      !isLoadingMessages
    ) {
      loadOlderMessages(conversationId);
    }
  }, [hasMoreMessages, isLoadingOlderMessages, isLoadingMessages, loadOlderMessages, conversationId]);

  const senderDisplayName = user
    ? `${user.first_name} ${user.last_name}`.trim() || user.username
    : "";

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be picked again
    if (!file || !user) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(t("chat.fileTooLarge"));
      return;
    }

    setUploadError(null);
    setUploadingName(file.name);
    try {
      const result = await uploadFile(file);
      await sendMessage({
        conversation_id: conversationId,
        sender_id: user.id,
        sender_name: senderDisplayName,
        content: "",
        type: "file",
        attachment_url: result.file_path,
        attachment_name: result.file_name,
        attachment_type: result.file_type,
        attachment_size: result.file_size,
      });
    } catch (err) {
      console.error("[ChatRoom] Upload error:", err);
      setUploadError(t("chat.uploadFailed"));
    } finally {
      setUploadingName(null);
    }
  };

  const openLightbox = (url: string, alt: string) => setLightbox({ url, alt });
  const closeLightbox = () => setLightbox(null);

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Chat header */}
      <div
        className={`px-4 py-3 border-b flex items-center gap-3 ${
          isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"
        }`}
      >
        <button
          onClick={onBack}
          className={`md:hidden p-2 rounded-lg ${
            isDarkMode ? "hover:bg-dark-hover text-gray-300" : "hover:bg-gray-100 text-gray-600"
          }`}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <button
          onClick={() => setShowDetails(true)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
        >
          <div className="relative flex-shrink-0">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center ${
                activeConversation?.trip_id
                  ? "bg-amber-500"
                  : activeConversation?.type === "group" ? "bg-violet-500" : "bg-primary-500"
              }`}
            >
              <FontAwesomeIcon
                icon={
                  activeConversation?.trip_id
                    ? faTruck
                    : activeConversation?.type === "group" ? faUsers : faUser
                }
                className="text-white text-sm"
              />
            </div>
            {isOtherOnline && !activeConversation?.trip_id && (
              <FontAwesomeIcon
                icon={faCircle}
                className="absolute bottom-0 right-0 text-green-500 text-[7px]"
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {convName}
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {activeConversation?.trip_id && activeConversation.route_name && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex items-center gap-1 ${
                  isDarkMode ? "bg-blue-500/20 text-blue-400" : "bg-blue-50 text-blue-700"
                }`}>
                  <FontAwesomeIcon icon={faRoute} className="text-[8px]" />
                  {activeConversation.route_name}
                </span>
              )}
              {activeConversation?.trip_id && !activeConversation.route_name && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"
                }`}>
                  {activeConversation.trip_name || activeConversation.trip_id}
                </span>
              )}
              <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {activeConversation?.trip_id
                  ? t("chat.tripParticipantsCount", { count: others.length + 1 })
                  : isOtherOnline ? t("chat.online") : t("chat.offline")}
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Messages area: min-h-0 so flex child can shrink and scroll correctly */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
          isDarkMode ? "bg-dark-bg" : "bg-gray-50"
        }`}
      >
        <div className="w-full mx-auto px-3 sm:px-4 py-3 flex flex-col min-h-full">
          {isLoadingOlderMessages && (
            <div className="flex items-center justify-center py-2 shrink-0">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500 text-sm" />
            </div>
          )}

          {!hasMoreMessages && messages.length > 0 && (
            <div className="flex items-center justify-center py-2 shrink-0">
              <span className={`text-[10px] ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>
                {t("chat.historyStart")}
              </span>
            </div>
          )}

          {isLoadingMessages && messages.length === 0 ? (
            <div className="flex flex-1 items-center justify-center min-h-[12rem]">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500 text-xl" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-1 items-center justify-center min-h-[12rem]">
              <p className={`text-sm text-center max-w-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {t("chat.startConversation")}
              </p>
            </div>
          ) : (
            <div className="flex flex-col flex-1 justify-end gap-0.5 pb-1">
          {messages.map((msg, idx) => {
            const isMe = msg.sender_id === user?.id;
            const prev = idx > 0 ? messages[idx - 1] : null;
            const sameSenderAsPrev =
              prev &&
              prev.type !== "system" &&
              msg.type !== "system" &&
              prev.sender_id === msg.sender_id;
            const rowSpacing = sameSenderAsPrev ? "mt-0.5" : "mt-2.5 first:mt-0";

            if (msg.type === "system") {
              return (
                <div key={msg.id} className="flex justify-center shrink-0 my-2">
                  <span className={`text-[11px] px-3 py-1 rounded-full max-w-[90%] text-center ${
                    isDarkMode ? "bg-dark-surface text-gray-400" : "bg-gray-200 text-gray-500"
                  }`}>
                    {msg.content}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={`flex w-full shrink-0 ${rowSpacing} ${isMe ? "justify-end pl-10 sm:pl-14" : "justify-start pr-10 sm:pr-14"}`}
              >
                <div
                  className={`max-w-[min(85%,28rem)] rounded-2xl px-3 py-2 ${
                    isMe
                      ? "bg-primary-500 text-white rounded-br-md rounded-tr-sm"
                      : isDarkMode
                        ? "bg-dark-surface text-gray-200 rounded-bl-md rounded-tl-sm border border-dark-border/60"
                        : "bg-white text-gray-800 rounded-bl-md rounded-tl-sm shadow-sm border border-gray-100"
                  }`}
                >
                  {!isMe && activeConversation?.type === "group" && (
                    <p className="text-[11px] font-medium mb-0.5 text-primary-500">
                      {msg.sender_name}
                    </p>
                  )}

                  {msg.attachment_url && (
                    <MessageAttachment
                      msg={msg}
                      isMe={isMe}
                      isDarkMode={isDarkMode}
                      onZoom={openLightbox}
                    />
                  )}

                  {msg.content && (
                    <p
                      className={`text-sm whitespace-pre-wrap break-words leading-relaxed ${
                        msg.attachment_url ? "mt-1.5" : ""
                      }`}
                    >
                      {msg.content}
                    </p>
                  )}

                  <div
                    className={`flex items-center gap-1 mt-1 justify-end text-[10px] ${
                      isMe ? "text-white/60" : isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    <span>
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {isMe && msg.status === "pending" && (
                      <FontAwesomeIcon icon={faClock} title={t("chat.statusPending")} />
                    )}
                    {isMe && msg.status === "sent" && (
                      <FontAwesomeIcon icon={faCheck} title={t("chat.statusSent")} />
                    )}
                    {isMe && msg.status === "failed" && (
                      <span className="flex items-center gap-1 text-red-300">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        <button
                          type="button"
                          onClick={() => retrySendMessage(msg.id)}
                          className="underline hover:text-white"
                          title={t("chat.retry")}
                        >
                          <FontAwesomeIcon icon={faRotateRight} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFailedMessage(msg.id)}
                          className="hover:text-white"
                          title={t("chat.discard")}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      </div>

      {/* Upload status bar */}
      {(uploadingName || uploadError) && (
        <div
          className={`px-4 py-2 text-xs flex items-center gap-2 border-t ${
            isDarkMode ? "bg-dark-surface border-dark-border" : "bg-gray-50 border-gray-200"
          }`}
        >
          {uploadingName && (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500" />
              <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                {t("chat.uploading", { name: uploadingName })}
              </span>
            </>
          )}
          {!uploadingName && uploadError && (
            <>
              <span className="text-red-500">{uploadError}</span>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                className={`ml-auto ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChosen}
      />
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,audio/*,video/*"
        className="hidden"
        onChange={handleFileChosen}
      />

      <ChatInput
        conversationId={conversationId}
        isDarkMode={isDarkMode}
        isUploading={!!uploadingName}
        onPickMedia={() => mediaInputRef.current?.click()}
        onPickImage={() => imageInputRef.current?.click()}
      />

      {showDetails && activeConversation && (
        <ConversationDetailsPanel
          conversation={activeConversation}
          onClose={() => setShowDetails(false)}
        />
      )}

      {lightbox && (
        <ImageLightbox
          url={lightbox.url}
          alt={lightbox.alt}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat input (isolated so typing doesn't re-render the message list)
// ---------------------------------------------------------------------------
function ChatInput({
  conversationId,
  isDarkMode,
  isUploading,
  onPickMedia,
  onPickImage,
}: {
  conversationId: string;
  isDarkMode: boolean;
  isUploading: boolean;
  onPickMedia: () => void;
  onPickImage: () => void;
}) {
  const { t } = useTranslation("support");
  const user = useUserStore((state) => state.user);
  const isSending = useSupportChatStore((s) => s.isSending);
  const sendMessage = useSupportChatStore((s) => s.sendMessage);
  const setTyping = useSupportChatStore((s) => s.setTyping);

  const [messageText, setMessageText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  const adjustTextareaHeight = useCallback(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    const next = Math.min(Math.max(ta.scrollHeight, CHAT_INPUT_MIN_PX), CHAT_INPUT_MAX_PX);
    ta.style.height = `${next}px`;
  }, []);

  useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [messageText, adjustTextareaHeight]);

  useEffect(() => {
    isTypingRef.current = false;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    requestAnimationFrame(() => adjustTextareaHeight());
  }, [conversationId, adjustTextareaHeight]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (isTypingRef.current && user?.id) {
      isTypingRef.current = false;
      setTyping(conversationId, user.id, false);
    }
  }, [conversationId, user?.id, setTyping]);

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || !user) return;

    const content = messageText.trim();
    setMessageText("");
    stopTyping();

    const senderDisplayName =
      `${user.first_name} ${user.last_name}`.trim() || user.username;

    await sendMessage({
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: senderDisplayName,
      content,
    });
  }, [messageText, user, conversationId, sendMessage, stopTyping]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);

    if (user?.id) {
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        setTyping(conversationId, user.id, true);
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        setTyping(conversationId, user.id, false);
      }, 2000);
    }
  };

  return (
    <div
      className={`px-4 py-3 border-t ${
        isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"
      }`}
    >
      <div
        className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-1.5 ${
          isDarkMode ? "bg-dark-hover" : "bg-gray-100"
        }`}
      >
        <button
          type="button"
          onClick={onPickMedia}
          disabled={isUploading}
          className={`p-1.5 rounded-full transition-colors disabled:opacity-50 ${
            isDarkMode ? "hover:bg-dark-border text-gray-400" : "hover:bg-gray-200 text-gray-500"
          }`}
          title={t("chat.attachFile")}
        >
          <FontAwesomeIcon icon={faPaperclip} className="text-sm" />
        </button>
        <button
          type="button"
          onClick={onPickImage}
          disabled={isUploading}
          className={`p-1.5 rounded-full transition-colors disabled:opacity-50 ${
            isDarkMode ? "hover:bg-dark-border text-gray-400" : "hover:bg-gray-200 text-gray-500"
          }`}
          title={t("chat.attachImage")}
        >
          <FontAwesomeIcon icon={faImage} className="text-sm" />
        </button>

        <textarea
          ref={inputRef}
          value={messageText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={t("chat.inputPlaceholder")}
          rows={1}
          className={`min-w-0 flex-1 max-h-24 resize-none overflow-y-auto border-0 bg-transparent py-2 text-sm leading-snug outline-none ring-0 focus:ring-0 ${
            isDarkMode ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"
          }`}
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={!messageText.trim() || isSending}
          className={`px-2 py-1 rounded-full transition-colors ${
            messageText.trim()
              ? "bg-primary-500 text-white hover:bg-primary-600"
              : isDarkMode
                ? "bg-dark-border text-gray-500"
                : "bg-gray-200 text-gray-400"
          }`}
        >
          {isSending ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment renderer
// ---------------------------------------------------------------------------
function MessageAttachment({
  msg,
  isMe,
  isDarkMode,
  onZoom,
}: {
  msg: ChatMessage;
  isMe: boolean;
  isDarkMode: boolean;
  onZoom: (url: string, alt: string) => void;
}) {
  const { t } = useTranslation("support");
  const url = msg.attachment_url;
  if (!url) return null;

  const type = msg.attachment_type ?? "";
  const name = msg.attachment_name ?? t("chat.defaultFileName");

  if (type.startsWith("image/")) {
    return (
      <button
        type="button"
        onClick={() => onZoom(url, name)}
        className="block rounded-lg overflow-hidden bg-black/10 cursor-zoom-in"
      >
        <img
          src={url}
          alt={name}
          className="max-w-[260px] max-h-[260px] object-cover"
          loading="lazy"
        />
      </button>
    );
  }

  if (type.startsWith("video/")) {
    return (
      <video
        controls
        preload="metadata"
        className="rounded-lg max-w-[280px] max-h-[280px] bg-black"
      >
        <source src={url} type={type} />
      </video>
    );
  }

  if (type.startsWith("audio/")) {
    return (
      <audio controls preload="metadata" className="w-[240px]">
        <source src={url} type={type} />
      </audio>
    );
  }

  // Fallback: generic file
  return (
    <button
      type="button"
      onClick={() => downloadRemoteFile(url, name)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs w-full text-left ${
        isMe
          ? "bg-white/15 hover:bg-white/25"
          : isDarkMode
            ? "bg-dark-hover hover:bg-dark-border"
            : "bg-gray-100 hover:bg-gray-200"
      }`}
      title={t("chat.downloadFileTitle", { name })}
    >
      <FontAwesomeIcon icon={faFile} className="text-sm flex-shrink-0" />
      <span className="truncate max-w-[180px]">{name}</span>
      <FontAwesomeIcon icon={faDownload} className="ml-auto text-[10px] opacity-70" />
      {msg.attachment_size ? (
        <span className="opacity-70">{formatBytes(msg.attachment_size)}</span>
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Image lightbox (full-screen viewer)
// ---------------------------------------------------------------------------
function ImageLightbox({
  url,
  alt,
  onClose,
}: {
  url: string;
  alt: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("support");
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label={t("chat.lightboxClose")}
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          downloadRemoteFile(url, alt);
        }}
        className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label={t("chat.lightboxDownload")}
      >
        <FontAwesomeIcon icon={faDownload} />
      </button>
      <img
        src={url}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain"
      />
    </div>,
    document.body,
  );
}
